import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import WebService from "../WebService";

// Types
interface AttendanceSummary {
  login_time: string | null;
  logout_time: string | null;
  work_hours: string | null;
  status: "Present" | "Absent";
}
interface AttendanceLog {
  date: string;
  check_in: string | null;
  check_out: string | null;
  work_hours: string | null;
  status: string;
}
interface LeaveStats {
  total: number;
  used: number;
  available: number;
}
interface LeaveRequest {
  leave_id: number;
  leave_type: string;
  status: "APPROVED" | "PENDING" | "REJECTED";
  reason: string;
  start_date: string;
  end_date: string;
  applied_at?: string;
}
interface TaskOverview {
  id: number;
  subject: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | string;
  status: string;
  end_date: string;
}
// NEW: MyDetails type for /my-details API
interface MyDetails {
  check_in_time: string | null;
  check_out_time: string | null;
  sick_leave_balance: number;
  casual_leave_balance: number;
}

interface AttendanceState {
  summary: AttendanceSummary | null;
  logs: AttendanceLog[];
  logsPagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
  };
  leaveStats: LeaveStats | null;
  recentLeaves: LeaveRequest[];
  tasks: TaskOverview[];
  loading: boolean;
  logsLoading: boolean;
  logsLoadingMore: boolean;
  leaveLoading: boolean;
  tasksLoading: boolean;
  canCheckIn: boolean;
  canCheckOut: boolean;
  clockedIn: boolean;
  optimisticCheckIn: boolean;
  myDetails: MyDetails | null;
}

const initialState: AttendanceState = {
  summary: null,
  logs: [],
  logsPagination: {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    page_size: 10,
  },
  leaveStats: null,
  recentLeaves: [],
  tasks: [],
  loading: false,
  logsLoading: false,
  logsLoadingMore: false,
  leaveLoading: false,
  tasksLoading: false,
  canCheckIn: false,
  canCheckOut: false,
  clockedIn: false,
  optimisticCheckIn: false,
  myDetails: null, 
};

// Async thunks

export const fetchAttendanceSummary = createAsyncThunk(
  "attendance/fetchSummary",
  async (_, { rejectWithValue }) => {
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/attendance/my?page_number=1&limit=15`,
      });
      const payload = res?.data ?? res;
      return payload?.attendance || [];
    } catch (err: any) {
      return rejectWithValue("Failed to load today's summary");
    }
  }
);

export const fetchAttendanceMyDetails = createAsyncThunk(
  "attendance/fetchAttendanceMyDetails",
  async (_, { rejectWithValue }) => {
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/attendance/my-details`,
      });
      return res?.data ?? res;
    } catch {
      return rejectWithValue("Could not load basic attendance details");
    }
  }
);

export const fetchAttendanceLogs = createAsyncThunk(
  "attendance/fetchLogs",
  async (
    { page }: { page: number; append?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/attendance/my?page_number=${page}&limit=10`,
      });
      return res?.data ?? res;
    } catch {
      return rejectWithValue("Could not load attendance logs");
    }
  }
);

export const fetchLeaves = createAsyncThunk(
  "attendance/fetchLeaves",
  async (_, { rejectWithValue }) => {
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/leave/my`,
      });

      return res.data;
    } catch {
      return rejectWithValue("Could not load leave data");
    }
  }
);

export const fetchTasks = createAsyncThunk(
  "attendance/fetchTasks",
  async (userId: number, { rejectWithValue }) => {
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/tasks?user_id=${userId}&limit=10&page_number=1`,
      });
      return res.data?.data?.tasks || [];
    } catch {
      return rejectWithValue("Could not load assigned tasks");
    }
  }
);

export const checkIn = createAsyncThunk(
  "attendance/checkIn",
  async (_, { rejectWithValue }) => {
    try {
      const res = await WebService.postAPI<any>({
        action: `/v1/door-management/attendance/check-in`,
        body: {},
      });
      const payload = res?.data ?? res;
      return {
        check_in_time: payload?.check_in_time ?? payload?.check_in ?? null,
        check_out_time: payload?.check_out_time ?? payload?.check_out ?? null,
        date: payload?.date ?? new Date().toISOString().slice(0, 10),
      };
    } catch {
      return rejectWithValue("Failed to check in.");
    }
  }
);

export const checkOut = createAsyncThunk(
  "attendance/checkOut",
  async (_, { rejectWithValue }) => {
    try {
      const res = await WebService.postAPI<any>({
        action: `/v1/door-management/attendance/check-out`,
        body: { note: "checkout" },
      });
      const payload = res?.data ?? res;
      return {
        check_in_time: payload?.check_in_time ?? payload?.check_in ?? null,
        check_out_time: payload?.check_out_time ?? payload?.check_out ?? null,
        date: payload?.date ?? new Date().toISOString().slice(0, 10),
      };
    } catch {
      return rejectWithValue("Failed to check out.");
    }
  }
);

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    resetOptimisticCheckIn(state) {
      state.optimisticCheckIn = false;
    },
    setOptimisticCheckIn(state) {
      state.optimisticCheckIn = true;
    },
    resetAttendance() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // Attendance summary fetch
      .addCase(fetchAttendanceSummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAttendanceSummary.fulfilled, (state, action) => {
        const attendanceList = action.payload;
        const todayStr = new Date().toISOString().slice(0, 10);
        const today = attendanceList.find((item: any) => {
          if (!item.date) return false;
          return (
            item.date.slice(0, 10) === todayStr || item.date === todayStr
          );
        }) || {};

        // Always trust the backend for this logged-in user
        state.optimisticCheckIn = false;
        state.summary = {
          login_time: today.check_in_time || today.check_in || null,
          logout_time: today.check_out_time || today.check_out || null,
          work_hours: today.work_hours || "--",
          status:
            today.status === "ABSENT" || today.status === "Absent"
              ? "Absent"
              : today.check_in_time || today.check_in
              ? "Present"
              : "Absent",
        };
        const hasIn = !!(today.check_in_time || today.check_in);
        const hasOut = !!(today.check_out_time || today.check_out);
        state.canCheckIn = !hasIn;
        state.canCheckOut = hasIn && !hasOut;
        state.clockedIn = hasIn && !hasOut;
        state.loading = false;
      })
      .addCase(fetchAttendanceSummary.rejected, (state) => {
        state.summary = null;
        state.canCheckIn = true;
        state.canCheckOut = false;
        state.clockedIn = false;
        state.loading = false;
      })

      .addCase(fetchAttendanceMyDetails.fulfilled, (state, action) => {
        state.myDetails = action.payload;
      })
      .addCase(fetchAttendanceMyDetails.rejected, (state) => {
        state.myDetails = null;
      })

      // Attendance logs
      .addCase(fetchAttendanceLogs.pending, (state, action) => {
        if (action.meta.arg.append) {
          state.logsLoadingMore = true;
        } else {
          state.logsLoading = true;
        }
      })
      .addCase(fetchAttendanceLogs.fulfilled, (state, action) => {
        const data = action.payload;
        const attendanceList = data?.attendance || [];
        const mapped = attendanceList.map((log: any) => ({
          date: log.date || "",
          check_in: log.check_in_time || log.check_in || null,
          check_out: log.check_out_time || log.check_out || null,
          work_hours: log.work_hours || "--",
          status: log.status === "PRESENT" || log.status === "Present" ? "Present" : "Absent",
        }));
        if (action.meta.arg.append) {
          const expectedPage = state.logsPagination.current_page + 1;
          if (state.logsLoading || action.meta.arg.page !== expectedPage) {
            state.logsLoadingMore = false;
            return;
          }
          const seen = new Set(state.logs.map((l) => l.date));
          state.logs = [...state.logs, ...mapped.filter((l: AttendanceLog) => !seen.has(l.date))];
        } else {
          state.logs = mapped;
        }
        state.logsPagination = {
          current_page: data?.pagination?.current_page || action.meta.arg.page || 1,
          total_pages: data?.pagination?.total_pages || 1,
          total_records: data?.pagination?.total_records || attendanceList.length,
          page_size: data?.pagination?.page_size || 10,
        };
        state.logsLoading = false;
        state.logsLoadingMore = false;
      })
      .addCase(fetchAttendanceLogs.rejected, (state, action) => {
        if (!action.meta.arg.append) {
          state.logs = [];
        }
        state.logsLoading = false;
        state.logsLoadingMore = false;
      })

      // Leaves
      .addCase(fetchLeaves.pending, (state) => {
        state.leaveLoading = true;
      })
.addCase(fetchLeaves.fulfilled, (state, action) => {
  const data = action.payload;
  
  state.leaveStats = {
    total: data?.pagination?.total_records ?? 0,
    used: 0, 
    available: 0, 
  };

  let leaves: LeaveRequest[] = [];
  if (Array.isArray(data?.leaves)) {
    leaves = [...data.leaves].sort(
      (a, b) =>
        new Date(b.applied_at || b.start_date).getTime() -
        new Date(a.applied_at || a.start_date).getTime()
    );
  }

  state.recentLeaves = leaves.slice(0, 3);
  state.leaveLoading = false;

})
      .addCase(fetchLeaves.rejected, (state) => {
        state.leaveStats = null;
        state.recentLeaves = [];
         console.log("✅ recentLeaves after reducer:", state.recentLeaves);
        state.leaveLoading = false;
      })

      // Tasks
      .addCase(fetchTasks.pending, (state) => {
        state.tasksLoading = true;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.tasks = action.payload.map((t: any) => ({
          id: t.id,
          subject: t.subject,
          status: t.status,
          priority: t.priority,
          end_date: t.end_date,
        }));
        state.tasksLoading = false;
      })
      .addCase(fetchTasks.rejected, (state) => {
        state.tasks = [];
        state.tasksLoading = false;
      })

      // Check In
      .addCase(checkIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkIn.fulfilled, (state, action) => {
        const { check_in_time, check_out_time, date } = action.payload;
        state.summary = {
          login_time: check_in_time,
          logout_time: check_out_time,
          work_hours: "--",
          status: "Present",
        };
        state.myDetails = {
          ...(state.myDetails || {
            sick_leave_balance: 0,
            casual_leave_balance: 0,
          }),
          check_in_time,
          check_out_time,
        };
        state.canCheckIn = false;
        state.canCheckOut = true;
        state.clockedIn = true;
        state.optimisticCheckIn = false;
        state.loading = false;

        state.logs = [
          {
            date: date,
            check_in: check_in_time,
            check_out: check_out_time,
            work_hours: "--",
            status: "Present",
          },
          ...state.logs.filter((l) => (l.date || "").slice(0, 10) !== date),
        ];
      })
      .addCase(checkIn.rejected, (state) => {
        state.loading = false;
      })

      // Check Out
      .addCase(checkOut.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkOut.fulfilled, (state, action) => {
        const { check_in_time, check_out_time, date } = action.payload;
        if (state.summary) {
          state.summary.login_time = check_in_time ?? state.summary.login_time;
          state.summary.logout_time = check_out_time;
        }
        if (state.myDetails) {
          state.myDetails.check_in_time = check_in_time ?? state.myDetails.check_in_time;
          state.myDetails.check_out_time = check_out_time;
        }
        state.canCheckOut = false;
        state.clockedIn = false;
        state.loading = false;
        const idx = state.logs.findIndex(
          (l) => (l.date || "").slice(0, 10) === date
        );
        if (idx >= 0) {
          state.logs[idx] = {
            ...state.logs[idx],
            check_in: check_in_time ?? state.logs[idx].check_in,
            check_out: check_out_time,
          };
        }
      })
      .addCase(checkOut.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetOptimisticCheckIn,
  setOptimisticCheckIn,
  resetAttendance,
} = attendanceSlice.actions;
export default attendanceSlice.reducer;
